import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Gift,
  MapPin,
  Minus,
  Plus,
  User,
  X,
} from "lucide-react";
import Axios from "../../services/AxiosInterceptor";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const DemoDetails = () => {
  const { id, locationName } = useParams();
  const [demo, setDemo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableResources, setAvailableResources] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState();
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [datePage, setDatePage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [stepMessage, setStepMessage] = useState({ text: "", visible: false });
  const [cxReview, setCxReview] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const showStepMessage = (text) => {
    setStepMessage({ text, visible: true });
    setTimeout(() => setStepMessage({ text: "", visible: false }), 2800);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const STORAGE_URL = "https://yogamatic.in/api/";

  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("Username");
  const email = localStorage.getItem("email");

  const maxSeats = useMemo(() => {
    if (!selectedSlot || !availableSlots.length) return 10;
    const slot = availableSlots.find((s) => s.time === selectedSlot);
    return slot?.remainingSeats || 10;
  }, [selectedSlot, availableSlots]);

  const DATES_PER_PAGE = 9;

  const paginatedDates = useMemo(() => {
    const sorted = [...availableDates].sort();
    const totalPages = Math.ceil(sorted.length / DATES_PER_PAGE);
    return { sorted, totalPages };
  }, [availableDates]);

  const visibleDates = useMemo(() => {
    const start = datePage * DATES_PER_PAGE;
    return paginatedDates.sorted.slice(start, start + DATES_PER_PAGE);
  }, [datePage, paginatedDates]);

  const currentMonthLabel = useMemo(() => {
    if (!visibleDates.length) return "";
    const first = new Date(visibleDates[0]);
    const last = new Date(visibleDates[visibleDates.length - 1]);
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
      return `${months[first.getMonth()]} ${first.getFullYear()}`;
    }
    return `${months[first.getMonth()]} - ${months[last.getMonth()]} ${last.getFullYear()}`;
  }, [visibleDates]);

  const formatDateCard = (dateStr) => {
    const d = new Date(dateStr);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return {
      dayName: days[d.getDay()],
      dateNum: d.getDate(),
      month: months[d.getMonth()],
    };
  };

  useEffect(() => {
    const fetchDemos = async () => {
      try {
        setLoader(true);
        const response = await Axios.get("api/get_demo_data");
        const data = response.data;

        if (data.success) {
          const filterDat = data.data.filter((item) => item._id === id);
          setDemo(filterDat[0]);

          const locations = filterDat?.[0]?.locations?.filter(
            (loc) => loc.name === locationName
          );

          const flattenedArray = locations?.flat() || [];
          setLocations(flattenedArray);
        }
      } catch (error) {
        console.error("Error fetching demos", error);
      } finally {
        setLoader(false);
      }
    };

    fetchDemos();
  }, [id, locationName]);

  const handleLocationChange = (locationName) => {
    const location = locations.find((loc) => loc.name === locationName);
    setSelectedLocation(locationName);
    setAvailableResources(location?.resources || []);
    setSelectedResource(null);
    setAvailableDates([]);
    setDatePage(0);
    setAvailableSlots([]);
    setStepMessage({ text: "", visible: false });
  };

  const handleResourceChange = (resource) => {
    setSelectedResource(resource);
    const location = locations.find((loc) => loc.name === selectedLocation);
    setAvailableDates(location?.sessions?.map((session) => session.date) || []);
    setSelectedDate(null);
    setDatePage(0);
    setAvailableSlots([]);
    setStepMessage({ text: "", visible: false });
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedSeats(1);
    const loc = locations.find((l) => l.name === selectedLocation);
    const session = loc?.sessions.find((s) => s.date === date);
    setAvailableSlots(session?.timeSlots || []);
    setStepMessage({ text: "", visible: false });
  };

  const handleBookClick = () => {
    if (!userId) {
      setShowModal(true);
      return;
    }
    handleDemoBooking();
  };

  const handleDemoBooking = async () => {
    if (!selectedLocation || !selectedDate || !selectedSlot || !selectedSeats) {
      alert("Please select Location, Resource, Date, Time Slot, and enter number of seats.");
      return;
    }

    try {
      const response = await Axios.post("api/book-demo", {
        userId: userId,
        demoId: demo._id,
        location: selectedLocation,
        date: selectedDate,
        time: selectedSlot,
        seatsBooked: parseInt(selectedSeats),
        email: email,
        mobile: localStorage.getItem("mobile"),
        resource: selectedResource,
        demoName: demo.title,
        username: username,
      });

      if (response.data.success) {
        setShowSuccessModal(true);
      } else {
        alert("Booking failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Booking Error:", error);
      alert("An error occurred while booking the demo.");
    }
  };

  if (loader) {
    return (
      <div className="ym-loader">
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: "4px solid var(--ym-mint)", borderTopColor: "var(--ym-sage)", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p>Loading demo details...</p>
        </div>
      </div>
    );
  }

  if (!demo) {
    return (
      <div className="ym-loader">
        <div className="ym-empty">Demo not found</div>
      </div>
    );
  }

  return (
    <main className="ym-page">
      {/* Login Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
            padding: 16,
          }}
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="ym-card ym-card-pad"
            style={{ maxWidth: 420, width: "100%", padding: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontWeight: 850, fontSize: 20 }}>Sign In Required</h3>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "var(--ym-muted)" }}
              >
                <X size={20} />
              </button>
            </div>
            <p className="ym-text" style={{ marginBottom: 24 }}>
              To book this free demo, please sign in to your account.
            </p>
            <div className="ym-actions" style={{ justifyContent: "flex-end" }}>
              <button className="ym-button ym-button-ghost" onClick={() => setShowModal(false)}>
                Close
              </button>
              <button
                className="ym-button ym-button-gold"
                onClick={() => {
                  localStorage.setItem("redirectAfterLogin", location.pathname);
                  navigate("/cx_login");
                }}
              >
                Sign In
                <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
            padding: 16,
          }}
          onClick={() => {
            setShowSuccessModal(false);
            navigate("/My_Bookings");
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="ym-card ym-card-pad"
            style={{ maxWidth: 480, width: "100%", padding: 36, textAlign: "center" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
            <h3 style={{ margin: "0 0 8px", fontWeight: 850, fontSize: 22 }}>Free Demo Session Confirmed</h3>
            <p className="ym-text" style={{ marginTop: 12 }}>
              Your free demo session has been successfully booked! We're excited to have you join us.
            </p>
            <p className="ym-text" style={{ marginTop: 8 }}>
              You will receive a confirmation email with all the details.
            </p>
            <div className="ym-actions" style={{ justifyContent: "center", marginTop: 24 }}>
              <button
                className="ym-button ym-button-gold"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/My_Bookings");
                }}
              >
                View My Bookings
                <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Hero Section */}
      <section className="ym-section" style={{ paddingBottom: 0 }}>
        <div className="ym-container">
          <motion.div
            className="ym-card"
            style={{
              borderRadius: "var(--ym-radius-lg)",
              boxShadow: "var(--ym-shadow)",
              border: "1px solid var(--ym-line)",
              overflow: "hidden",
            }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={fadeUp}
          >
            <div className="ym-detail-hero-grid">
              <div style={{ padding: "28px 28px" }}>
                <span className="ym-pill" style={{ marginBottom: 10, display: "inline-block" }}>
                  {demo.classMode || "Yoga class"} / {demo.sessionType || "Session"}
                </span>
                <h1 className="ym-title" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", margin: "8px 0 12px" }}>
                  {demo.title}
                </h1>
                {demo.benefits?.length > 0 && (
                  <ul className="ym-check-list" style={{ marginTop: 12 }}>
                    {demo.benefits.map((benefit, index) => (
                      <li key={index}>
                        <CheckCircle2 size={15} style={{ color: "#22a06b" }} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div style={{ position: "relative", minHeight: 220 }}>
                <img
                  src={`${STORAGE_URL}upload/${demo.image}`}
                  alt={demo.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Description & Benefits Section */}
      {demo.description && (
        <section className="ym-section" style={{ paddingTop: 32 }}>
          <div className="ym-container">
            <motion.div
              className="ym-card ym-card-pad"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              style={{ padding: 28 }}
            >
              <p className="ym-text" style={{ marginBottom: 20, lineHeight: 1.7 }}>
                {demo.description}
              </p>
              <h4 style={{ fontWeight: 850, fontSize: 16, marginBottom: 12, color: "var(--ym-sage)" }}>
                <CheckCircle2 size={16} style={{ marginRight: 8, verticalAlign: -2 }} />
                Benefits
              </h4>
              <ul className="ym-check-list">
                <li>
                  <MapPin size={15} />
                  <span><strong>Mode:</strong> {demo.classMode}</span>
                </li>
                <li>
                  <User size={15} />
                  <span><strong>Type:</strong> {demo.sessionType}</span>
                </li>
                <li>
                  <Gift size={15} />
                  <span><strong>Complimentary</strong> introduction session</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>
      )}

      {/* Booking Section */}
      <section className="ym-section" style={{ paddingTop: 0, paddingBottom: 40 }}>
        <div className="ym-container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={fadeUp}
            style={{ textAlign: "center", marginBottom: 0 }}
          >
            <span className="ym-eyebrow" style={{ marginInline: "auto" }}>
              <Calendar size={14} />
              Book your free session
            </span>
            <h2 className="ym-title-small">Reserve your complimentary demo.</h2>
          </motion.div>

          {/* Main Booking Card */}
          <motion.div
            className="ym-card"
            style={{
              borderRadius: "var(--ym-radius-lg)",
              boxShadow: "var(--ym-shadow)",
              border: "1px solid var(--ym-line)",
              overflow: "hidden",
              background: "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,253,247,0.98))",
            }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.05 }}
            variants={fadeUp}
          >
            {/* Booking Content */}
            <div style={{ padding: "28px 24px" }}>
                <motion.div className="ym-booking-shell">
              {/* Step Message Banner */}
              <AnimatePresence>
                {stepMessage.visible && (
                  <motion.div
                    initial={{ opacity: 0, y: -12, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -12, height: 0 }}
                  >
                    <div
                      style={{
                        padding: "10px 16px",
                        borderRadius: "var(--ym-radius)",
                        background: "rgba(245,182,66,0.12)",
                        border: "1px solid rgba(245,182,66,0.3)",
                        marginBottom: 16,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#8a6d1f",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Clock size={15} />
                      {stepMessage.text}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step 1: Location — always enabled */}
              <div style={{ marginBottom: 18 }}>
                <label className="ym-label">
                  <MapPin size={14} />
                  Step 1 — Choose Location
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 8 }}>
                  {locations?.map((loc) => (
                    <motion.button
                      key={loc.name}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleLocationChange(loc.name)}
                      className="ym-button"
                      style={{
                        justifyContent: "flex-start",
                        background: selectedLocation === loc.name
                          ? "linear-gradient(135deg, var(--ym-gold), #e88a35)"
                          : "rgba(255,255,255,0.72)",
                        borderColor: selectedLocation === loc.name
                          ? "transparent"
                          : "rgba(38,66,54,0.16)",
                        color: selectedLocation === loc.name ? "#20160d" : "var(--ym-ink)",
                        boxShadow: selectedLocation === loc.name
                          ? "0 8px 22px rgba(245,182,66,0.28)"
                          : "none",
                        minHeight: 52,
                        borderRadius: "var(--ym-radius)",
                      }}
                    >
                      <MapPin size={17} />
                      <span style={{ fontWeight: 750 }}>{loc.name}</span>
                      {selectedLocation === loc.name && <CheckCircle2 size={16} style={{ marginLeft: "auto" }} />}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Step 2: Resource — always visible */}
              <div style={{ marginBottom: 18 }}>
                <label className="ym-label">
                  <User size={15} />
                  Step 2 — Choose Resource
                </label>
                {availableResources.length > 0 ? (
                  <div style={{ position: "relative", marginTop: 8 }}>
                    <div style={{ filter: selectedLocation ? "none" : "blur(5px)", pointerEvents: selectedLocation ? "auto" : "none", transition: "filter 0.3s", userSelect: selectedLocation ? "auto" : "none" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                        {availableResources.map((res) => (
                          <motion.button
                            key={res}
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleResourceChange(res)}
                            className="ym-button"
                            style={{
                              justifyContent: "flex-start",
                              background: selectedResource === res
                                ? "linear-gradient(135deg, var(--ym-sage), #1b3027)"
                                : "rgba(255,255,255,0.72)",
                              borderColor: selectedResource === res
                                ? "transparent"
                                : "rgba(38,66,54,0.16)",
                              color: selectedResource === res ? "#fff" : "var(--ym-ink)",
                              boxShadow: selectedResource === res
                                ? "0 8px 22px rgba(38,66,54,0.22)"
                                : "none",
                              minHeight: 52,
                              borderRadius: "var(--ym-radius)",
                            }}
                          >
                            <User size={17} />
                            <span style={{ fontWeight: 750 }}>{res}</span>
                            {selectedResource === res && <CheckCircle2 size={16} style={{ marginLeft: "auto" }} />}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                    {!selectedLocation && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "auto", zIndex: 2 }}
                        onClick={() => showStepMessage("Please select a location first to see available resources.")}
                      >
                        <motion.div
                          initial={{ scale: 0.92 }}
                          animate={{ scale: 1 }}
                          className="ym-card ym-card-pad"
                          style={{ padding: "8px 18px", cursor: "pointer", background: "rgba(255,255,255,0.95)", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", border: "1px solid rgba(245,182,66,0.35)" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#8a6d1f" }}>
                            <MapPin size={15} />
                            Select a location first
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, padding: "20px 16px", textAlign: "center", borderRadius: "var(--ym-radius)", border: "1px dashed var(--ym-line)", background: "rgba(38,66,54,0.03)", fontSize: 13, color: "var(--ym-muted)" }}>
                    {selectedLocation ? "No resources available for this location." : "Select a location to see available resources."}
                  </div>
                )}
              </div>

              {/* Step 3: Date Carousel — always visible */}
              <div style={{ marginBottom: 18, opacity: selectedResource ? 1 : 0.5, transition: "opacity 0.3s" }}>
                <label className="ym-label">
                  <Calendar size={15} />
                  Step 3 — Pick a Date
                </label>
                {availableDates.length > 0 ? (
                  <div style={{ position: "relative", marginTop: 8 }}>
                    <div style={{ filter: selectedResource ? "none" : "blur(5px)", pointerEvents: selectedResource ? "auto" : "none", transition: "filter 0.3s", userSelect: selectedResource ? "auto" : "none" }}>
                      <motion.div
                        style={{
                          background: "rgba(255,248,237,0.5)",
                          borderRadius: "var(--ym-radius-lg)",
                          padding: "10px 10px",
                          border: "1px solid rgba(245,182,66,0.15)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 14,
                            padding: "0 4px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span className="ym-pill" style={{ fontSize: 12 }}>
                              <Calendar size={13} />
                              {paginatedDates.sorted.length} dates
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: "var(--ym-sage)" }}>
                              {currentMonthLabel}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setDatePage(Math.max(0, datePage - 1))}
                              disabled={datePage === 0}
                              className="ym-icon-box"
                              style={{
                                width: 34, height: 34,
                                border: "1px solid rgba(38,66,54,0.12)",
                                background: datePage === 0 ? "rgba(38,66,54,0.04)" : "rgba(255,255,255,0.82)",
                                cursor: datePage === 0 ? "not-allowed" : "pointer",
                                opacity: datePage === 0 ? 0.4 : 1,
                              }}
                            >
                              <ChevronLeft size={16} />
                            </motion.button>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setDatePage(Math.min(paginatedDates.totalPages - 1, datePage + 1))}
                              disabled={datePage >= paginatedDates.totalPages - 1}
                              className="ym-icon-box"
                              style={{ width: 34, height: 34, border: "1px solid rgba(38,66,54,0.12)", background: datePage >= paginatedDates.totalPages - 1 ? "rgba(38,66,54,0.04)" : "rgba(255,255,255,0.82)", cursor: datePage >= paginatedDates.totalPages - 1 ? "not-allowed" : "pointer", opacity: datePage >= paginatedDates.totalPages - 1 ? 0.4 : 1 }}
                            >
                              <ChevronRight size={16} />
                            </motion.button>
                          </div>
                        </div>
                        <motion.div
                          className="ym-grid-3"
                          style={{ gap: 10 }}
                          variants={stagger}
                          initial="hidden"
                          animate="show"
                          key={datePage}
                        >
                          {visibleDates.map((dateStr) => {
                            const fmt = formatDateCard(dateStr);
                            const selected = selectedDate === dateStr;
                            const hasSlots = paginatedDates.sorted.includes(dateStr);
                            return (
                              <motion.button
                                key={dateStr}
                                variants={fadeUp}
                                whileHover={!selected ? { scale: 1.03, y: -3 } : {}}
                                whileTap={!selected ? { scale: 0.96 } : {}}
                                onClick={() => hasSlots && handleDateChange(dateStr)}
                                className="ym-card"
                                style={{
                                  padding: "12px 8px",
                                  textAlign: "center",
                                  cursor: hasSlots ? "pointer" : "not-allowed",
                                  border: selected ? "2px solid var(--ym-gold)" : "1px solid var(--ym-line)",
                                  background: selected ? "linear-gradient(135deg, rgba(255,248,237,0.95), rgba(255,253,247,0.98))" : "rgba(255,255,255,0.82)",
                                  opacity: hasSlots ? 1 : 0.45,
                                }}
                              >
                                <div style={{ fontSize: 12, fontWeight: 800, color: selected ? "var(--ym-gold)" : "var(--ym-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{fmt.dayName}</div>
                                <div style={{ fontSize: 28, fontWeight: 850, color: selected ? "var(--ym-sage)" : "var(--ym-ink)", lineHeight: 1.15, marginTop: 4 }}>{fmt.dateNum}</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: selected ? "var(--ym-sage)" : "#7a8a80", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.02em" }}>{fmt.month}</div>
                                {selected && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ marginTop: 6 }}><CheckCircle2 size={16} style={{ color: "var(--ym-gold)" }} /></motion.div>}
                              </motion.button>
                            );
                          })}
                        </motion.div>
                        {paginatedDates.totalPages > 1 && (
                          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14 }}>
                            {Array.from({ length: paginatedDates.totalPages }, (_, i) => (
                              <motion.div key={i} whileTap={{ scale: 0.8 }} onClick={() => setDatePage(i)} style={{ width: 8, height: 8, borderRadius: "50%", background: i === datePage ? "var(--ym-gold)" : "rgba(38,66,54,0.12)", cursor: "pointer", transition: "background 0.2s" }} />
                            ))}
                          </div>
                        )}
                      </motion.div>
                    </div>
                    {!selectedResource && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "auto", zIndex: 2 }}
                        onClick={() => showStepMessage("Please select a resource first to see available dates.")}
                      >
                        <motion.div
                          initial={{ scale: 0.92 }}
                          animate={{ scale: 1 }}
                          className="ym-card ym-card-pad"
                          style={{ padding: "8px 18px", cursor: "pointer", background: "rgba(255,255,255,0.95)", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", border: "1px solid rgba(245,182,66,0.35)" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#8a6d1f" }}>
                            <User size={15} />
                            Select a resource first
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, padding: "20px 16px", textAlign: "center", borderRadius: "var(--ym-radius)", border: "1px dashed var(--ym-line)", background: "rgba(38,66,54,0.03)", fontSize: 13, color: "var(--ym-muted)" }}>
                    {selectedResource ? "No dates available for this resource." : "Select a resource to see available dates."}
                  </div>
                )}
              </div>

              {/* Step 4: Time Slots — always visible */}
              <div style={{ marginBottom: 18 }}>
                <label className="ym-label">
                  <Clock size={15} />
                  Step 4 — Choose Time Slot
                </label>
                {availableSlots.length > 0 ? (
                  <div style={{ position: "relative", marginTop: 8 }}>
                    <div style={{ filter: selectedDate ? "none" : "blur(5px)", pointerEvents: selectedDate ? "auto" : "none", transition: "filter 0.3s", userSelect: selectedDate ? "auto" : "none" }}>
                      <motion.div
                        className="ym-grid-3"
                        style={{ gap: 10 }}
                        variants={stagger}
                        initial="hidden"
                        animate="show"
                      >
                        {availableSlots.map((slot) => {
                          const full = slot.remainingSeats === 0;
                          return (
                            <motion.button
                              key={slot.time}
                              variants={fadeUp}
                              whileHover={full ? {} : { scale: 1.03, y: -3 }}
                              whileTap={full ? {} : { scale: 0.97 }}
                              onClick={() => !full && (setSelectedSlot(slot.time), setSelectedSeats(1))}
                              disabled={full}
                              className="ym-card ym-card-pad"
                              style={{
                                padding: "14px 12px",
                                textAlign: "center",
                                cursor: full ? "not-allowed" : "pointer",
                                border: selectedSlot === slot.time ? "2px solid var(--ym-gold)" : full ? "1px solid rgba(38,66,54,0.08)" : "1px solid var(--ym-line)",
                                background: selectedSlot === slot.time ? "linear-gradient(135deg, rgba(255,248,237,0.95), rgba(255,253,247,0.98))" : full ? "rgba(38,66,54,0.04)" : "rgba(255,255,255,0.82)",
                                opacity: full ? 0.5 : 1,
                              }}
                            >
                              <div style={{ fontSize: 17, fontWeight: 850, color: full ? "var(--ym-muted)" : "var(--ym-ink)" }}>{slot.time}</div>
                              <div style={{ fontSize: 12, color: full ? "#d94141" : "var(--ym-muted)", fontWeight: 700, marginTop: 4 }}>{full ? "Fully Booked" : `${slot.remainingSeats} seats`}</div>
                              {selectedSlot === slot.time && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ marginTop: 6 }}><CheckCircle2 size={18} style={{ color: "var(--ym-gold)" }} /></motion.div>}
                            </motion.button>
                          );
                        })}
                      </motion.div>
                    </div>
                    {!selectedDate && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "auto", zIndex: 2 }}
                        onClick={() => showStepMessage("Please pick a date first to see available time slots.")}
                      >
                        <motion.div
                          initial={{ scale: 0.92 }}
                          animate={{ scale: 1 }}
                          className="ym-card ym-card-pad"
                          style={{ padding: "8px 18px", cursor: "pointer", background: "rgba(255,255,255,0.95)", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", border: "1px solid rgba(245,182,66,0.35)" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#8a6d1f" }}>
                            <Calendar size={15} />
                            Select a date first
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, padding: "20px 16px", textAlign: "center", borderRadius: "var(--ym-radius)", border: "1px dashed var(--ym-line)", background: "rgba(38,66,54,0.03)", fontSize: 13, color: "var(--ym-muted)" }}>
                    {selectedDate ? "No time slots available for this date." : "Select a date to see available time slots."}
                  </div>
                )}
              </div>

              {/* Step 5: Seats — always visible */}
              <div style={{ marginBottom: 8 }}>
                <label className="ym-label">
                  <User size={15} />
                  Step 5 — Number of Seats
                </label>
                <div style={{ position: "relative" }}>
                  <div style={{ filter: selectedSlot ? "none" : "blur(5px)", pointerEvents: selectedSlot ? "auto" : "none", transition: "filter 0.3s" }}>
                    <motion.div
                      initial={{ scale: 0.96, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, justifyContent: "center" }}
                    >
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setSelectedSeats(Math.max(1, selectedSeats - 1))}
                        className="ym-icon-box"
                        style={{ width: 48, height: 48, cursor: "pointer", border: "none" }}
                        disabled={selectedSeats <= 1}
                      >
                        <Minus size={20} />
                      </motion.button>
                      <motion.div
                        key={selectedSeats}
                        initial={{ scale: 1.3, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="ym-card ym-card-pad"
                        style={{
                          padding: "12px 32px",
                          textAlign: "center",
                          minWidth: 100,
                          background: "linear-gradient(135deg, rgba(255,248,237,0.9), rgba(255,253,247,0.95))",
                          border: "2px solid rgba(245,182,66,0.3)",
                        }}
                      >
                        <div style={{ fontSize: 28, fontWeight: 850, color: "var(--ym-sage)" }}>
                          {selectedSeats}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ym-muted)" }}>
                          seat{selectedSeats > 1 ? "s" : ""}
                        </div>
                      </motion.div>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setSelectedSeats(Math.min(maxSeats, selectedSeats + 1))}
                        className="ym-icon-box"
                        style={{ width: 48, height: 48, cursor: "pointer", border: "none" }}
                        disabled={selectedSeats >= maxSeats}
                      >
                        <Plus size={20} />
                      </motion.button>
                    </motion.div>
                    <p className="ym-text" style={{ textAlign: "center", fontSize: 12, marginTop: 8 }}>
                      Max {maxSeats} seat{maxSeats > 1 ? "s" : ""} available
                    </p>
                  </div>
                  {!selectedSlot && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "auto", zIndex: 2 }}
                      onClick={() => showStepMessage("Please choose a time slot first to select seats.")}
                    >
                      <motion.div
                        initial={{ scale: 0.92 }}
                        animate={{ scale: 1 }}
                        className="ym-card ym-card-pad"
                        style={{ padding: "8px 18px", cursor: "pointer", background: "rgba(255,255,255,0.95)", boxShadow: "0 8px 30px rgba(0,0,0,0.1)", border: "1px solid rgba(245,182,66,0.35)" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#8a6d1f" }}>
                          <Clock size={15} />
                          Select a time slot first
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Reviews — compact card inside booking section */}
              {cxReview.length > 0 && (() => {
                const totalRating = cxReview.reduce((sum, r) => sum + r.rating, 0);
                const avgRating = totalRating / cxReview.length;
                const sortedReviews = [...cxReview].sort((a, b) => b.rating - a.rating);
                const visibleReviews = showAll ? sortedReviews : sortedReviews.slice(0, 3);
                const fullStars = Math.round(avgRating);
                return (
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    style={{ gridColumn: "1 / -1", marginTop: 20, borderTop: "1px solid var(--ym-line)", paddingTop: 18 }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 20, lineHeight: 1 }}>{"⭐".repeat(fullStars)}</span>
                        <span style={{ fontWeight: 800, fontSize: 15, color: "var(--ym-ink)" }}>{avgRating.toFixed(1)}</span>
                        <span style={{ fontSize: 12, color: "var(--ym-muted)", fontWeight: 600 }}>({cxReview.length} review{cxReview.length > 1 ? "s" : ""})</span>
                      </div>
                      {sortedReviews.length > 3 && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowAll(!showAll)}
                          className="ym-button"
                          style={{ padding: "6px 14px", minHeight: 0, fontSize: 12, fontWeight: 700, background: "rgba(38,66,54,0.06)", borderColor: "transparent" }}
                        >
                          {showAll ? "Show Less" : "See All"}
                        </motion.button>
                      )}
                    </div>
                    <div style={{ display: "grid", gap: 10 }}>
                      {visibleReviews.map((item, index) => {
                        const d = new Date(item.date);
                        return (
                          <motion.div
                            key={item._id || index}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="ym-review-card"
                            style={{ padding: "14px 16px" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, var(--ym-sage), #1b3027)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                                  {(item.username || "U")[0].toUpperCase()}
                                </div>
                                <div>
                                  <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{item.username || "Unknown User"}</p>
                                  <p style={{ margin: 0, fontSize: 11, color: "var(--ym-muted)" }}>{d.toLocaleDateString()}</p>
                                </div>
                              </div>
                              <span style={{ fontSize: 14, color: "#ffc107", flexShrink: 0 }}>{"⭐".repeat(item.rating)}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--ym-ink)", fontStyle: "italic" }}>
                              “{item.message}”
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })()}

              {/* Book button */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                style={{ gridColumn: "1 / -1", marginTop: 20 }}
              >
                {userId && selectedLocation && selectedResource && selectedDate && selectedSlot && selectedSeats ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleBookClick}
                    className="ym-button ym-button-gold"
                    style={{ width: "100%", justifyContent: "center", minHeight: 52, fontSize: 16 }}
                  >
                    <Gift size={18} />
                    Book Free Demo Now
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      if (!userId) {
                        localStorage.setItem("redirectAfterLogin", location.pathname);
                        navigate("/cx_login");
                        return;
                      }
                      showStepMessage("Please complete all steps above to book.");
                    }}
                    className="ym-button ym-button-primary"
                    style={{ width: "100%", justifyContent: "center", minHeight: 52, fontSize: 16, opacity: userId ? 0.5 : 1 }}
                  >
                    {userId ? "Complete Selection to Book" : "Sign In to Book"}
                    <ArrowRight size={18} />
                  </motion.button>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
      </section>
    </main>
  );
};

export default DemoDetails;
